import { useState, useEffect } from 'react';
import { useEventCallback } from '../../hooks/use-event-callback';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ordersAPI, couriersAPI } from '../../lib/api';
import { fetchWithCache } from '../../lib/cache';
import { toast } from 'sonner';
import { Search, Eye, RefreshCcw, Package, CheckCircle, XCircle } from 'lucide-react';

export default function AdminReturns() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('return_requested');
  const [showDialog, setShowDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [processingReturn, setProcessingReturn] = useState(false);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(20);

  const fetchData = useEventCallback(async (pageNum = page) => {
    try {
      const params = {
        page: pageNum,
        limit,
        search: search || undefined,
        source: 'online', // Only online customer orders
        status: statusFilter, // Filter by return status
        has_returns: true // Only orders with return requests
      };

      // Fetch orders with cache
      const cacheKey = `admin_returns_${statusFilter}_${search}_${pageNum}_${limit}`;

      await fetchWithCache(
        cacheKey,
        async () => {
          const res = await ordersAPI.getReturns(params); // Dedicated returns API
          return res.data;
        },
        (data, isFromCache) => {
          if (data) {
            setOrders(data.orders || []);
            setTotalPages(data.pages || 1);
          }
        }
      );
    } catch (error) {
      console.error('Failed to fetch returns:', error);
    } finally {
      setLoading(false);
    }
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      fetchData(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [search, fetchData]);

  useEffect(() => {
    setPage(1);
    fetchData(1);
  }, [statusFilter, fetchData]);

  const handleViewReturn = (order) => {
    setSelectedOrder(order);
    setShowDialog(true);
  };

  const handleApproveReturn = async (order) => {
    if (!window.confirm(`Approve return request for Order #${order.order_number}?`)) return;

    setProcessingReturn(order.id);
    try {
      // Call delivery partner API to schedule pickup
      const response = await couriersAPI.scheduleReturnPickup({
        order_id: order.id,
        pickup_address: order.shipping_address, // Customer address
        return_address: {
          // Your business address for returns
          name: "Amorlias Mart",
          phone: "+919462252525",
          address: "Business Return Address"
        },
        return_items: order.return_items,
        reason: order.return_reason
      });

      if (response.data.success) {
        // Update order status to return_approved
        await ordersAPI.updateReturnStatus(order.id, {
          status: 'return_approved',
          return_tracking_number: response.data.return_awb,
          pickup_scheduled_date: response.data.pickup_date,
          notes: 'Return pickup scheduled with delivery partner'
        });

        toast.success(`Return approved! Pickup AWB: ${response.data.return_awb}`);
        fetchData();
      } else {
        throw new Error(response.data.message || 'Failed to schedule pickup');
      }
    } catch (error) {
      console.error('Return approval error:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve return');
    } finally {
      setProcessingReturn(false);
    }
  };

  const handleRejectReturn = async (order) => {
    if (!window.confirm(`Reject return request for Order #${order.order_number}?`)) return;

    try {
      await ordersAPI.updateReturnStatus(order.id, {
        status: 'return_rejected',
        notes: 'Return request rejected by admin'
      });

      toast.success('Return request rejected');
      fetchData();
    } catch (error) {
      console.error('Return rejection error:', error);
      toast.error('Failed to reject return');
    }
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      return_requested: 'bg-amber-500/20 text-amber-400',
      return_approved: 'bg-blue-500/20 text-blue-400',
      return_picked: 'bg-indigo-500/20 text-indigo-400',
      return_received: 'bg-emerald-500/20 text-emerald-400',
      return_rejected: 'bg-red-500/20 text-red-400',
      refund_processed: 'bg-green-500/20 text-green-400',
    };
    return classes[status] || 'bg-slate-500/20 text-slate-400';
  };

  const getReturnStatusLabel = (status) => {
    const labels = {
      return_requested: 'Return Requested',
      return_approved: 'Return Approved',
      return_picked: 'Picked Up',
      return_received: 'Received',
      return_rejected: 'Rejected',
      refund_processed: 'Refund Processed',
    };
    return labels[status] || status;
  };

  return (
    <div className="space-y-6" data-testid="admin-returns">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Customer Returns</h1>
          <p className="text-slate-400">{orders.length} return requests</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={async () => {
              setRefreshing(true);
              await fetchData();
              setRefreshing(false);
            }}
            className="border-slate-600 hover:bg-slate-700"
          >
            <RefreshCcw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        {/* Status Filter */}
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[200px] bg-slate-800 border-slate-700">
            <SelectValue placeholder="Return Status" />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700">
            <SelectItem value="return_requested">Return Requested</SelectItem>
            <SelectItem value="return_approved">Return Approved</SelectItem>
            <SelectItem value="return_picked">Picked Up</SelectItem>
            <SelectItem value="return_received">Received</SelectItem>
            <SelectItem value="return_rejected">Rejected</SelectItem>
            <SelectItem value="refund_processed">Refund Processed</SelectItem>
          </SelectContent>
        </Select>

        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by order #, phone, or customer name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 input-admin"
          />
        </div>
      </div>

      {/* Returns Table */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-700 hover:bg-transparent">
                <TableHead className="text-slate-400">Order #</TableHead>
                <TableHead className="text-slate-400">Date</TableHead>
                <TableHead className="text-slate-400">Customer</TableHead>
                <TableHead className="text-slate-400">Return Reason</TableHead>
                <TableHead className="text-slate-400">Items</TableHead>
                <TableHead className="text-slate-400">Amount</TableHead>
                <TableHead className="text-slate-400">Status</TableHead>
                <TableHead className="text-slate-400 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">Loading...</TableCell>
                </TableRow>
              ) : orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-slate-400">
                    <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    No return requests found
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((order) => (
                  <TableRow key={order.id} className="border-slate-700">
                    <TableCell className="font-mono font-medium">{order.order_number}</TableCell>
                    <TableCell className="text-slate-400">
                      {new Date(order.return_requested_at || order.created_at).toLocaleDateString('en-IN')}
                    </TableCell>
                    <TableCell>{order.customer_name || order.customer_phone || '-'}</TableCell>
                    <TableCell className="max-w-[200px] truncate">{order.return_reason || 'Not specified'}</TableCell>
                    <TableCell>{order.return_items?.length || order.items.length} items</TableCell>
                    <TableCell className="font-semibold">₹{(order.return_amount || order.grand_total).toLocaleString()}</TableCell>
                    <TableCell>
                      <Badge className={getStatusBadgeClass(order.return_status || order.status)}>
                        {getReturnStatusLabel(order.return_status || order.status)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" onClick={() => handleViewReturn(order)} title="View Details">
                          <Eye className="w-4 h-4" />
                        </Button>
                        {(order.return_status === 'return_requested' || order.status === 'return_requested') && (
                          <>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => handleApproveReturn(order)}
                              disabled={processingReturn === order.id}
                              title="Approve Return"
                              className="text-green-400 hover:text-green-300 hover:bg-green-400/10"
                            >
                              {processingReturn === order.id ? (
                                <RefreshCcw className="w-4 h-4 animate-spin" />
                              ) : (
                                <CheckCircle className="w-4 h-4" />
                              )}
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => handleRejectReturn(order)}
                              title="Reject Return"
                              className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination Controls */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-400">
          Page {page} of {totalPages}
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const newPage = Math.max(1, page - 1);
              setPage(newPage);
              fetchData(newPage);
            }}
            disabled={page <= 1}
            className="border-slate-700 hover:bg-slate-800"
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const newPage = Math.min(totalPages, page + 1);
              setPage(newPage);
              fetchData(newPage);
            }}
            disabled={page >= totalPages}
            className="border-slate-700 hover:bg-slate-800"
          >
            Next
          </Button>
        </div>
      </div>

      {/* Return Detail Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle>Return Request - Order #{selectedOrder?.order_number}</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-6">
              {/* Return Information */}
              <div>
                <h4 className="font-semibold mb-3">Return Information</h4>
                <div className="p-3 bg-slate-700/50 rounded-lg space-y-2">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Reason:</span>
                    <span>{selectedOrder.return_reason || 'Not specified'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Requested Date:</span>
                    <span>{new Date(selectedOrder.return_requested_at || selectedOrder.created_at).toLocaleDateString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Status:</span>
                    <Badge className={getStatusBadgeClass(selectedOrder.return_status || selectedOrder.status)}>
                      {getReturnStatusLabel(selectedOrder.return_status || selectedOrder.status)}
                    </Badge>
                  </div>
                  {selectedOrder.return_tracking_number && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Return AWB:</span>
                      <span className="font-mono">{selectedOrder.return_tracking_number}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Return Items */}
              <div>
                <h4 className="font-semibold mb-3">Items to Return</h4>
                <div className="space-y-2">
                  {(selectedOrder.return_items || selectedOrder.items)?.map((item, index) => (
                    <div key={index} className="flex justify-between p-3 bg-slate-700/50 rounded-lg">
                      <div>
                        <p className="font-medium">{item.product_name || 'Unknown Product'}</p>
                        <p className="text-sm text-slate-400">
                          SKU: {item.sku || 'N/A'} | Qty: {item.return_quantity || item.quantity || 0} | Unit: ₹{(item.price || 0).toLocaleString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">₹{((item.return_quantity || item.quantity) * item.price || 0).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-slate-700 flex justify-between font-bold">
                  <span>Return Amount</span>
                  <span>₹{(selectedOrder.return_amount || selectedOrder.grand_total).toLocaleString()}</span>
                </div>
              </div>

              {/* Customer Information */}
              <div>
                <h4 className="font-semibold mb-3">Customer Information</h4>
                <div className="p-3 bg-slate-700/50 rounded-lg text-sm">
                  <p className="font-medium">{selectedOrder.shipping_address?.name || 'N/A'}</p>
                  <p className="text-slate-400">{selectedOrder.shipping_address?.line1 || ''}</p>
                  {selectedOrder.shipping_address?.line2 && <p className="text-slate-400">{selectedOrder.shipping_address.line2}</p>}
                  <p className="text-slate-400">
                    {selectedOrder.shipping_address?.city || ''}, {selectedOrder.shipping_address?.state || ''} - {selectedOrder.shipping_address?.pincode || ''}
                  </p>
                  <p className="text-slate-400">Phone: {selectedOrder.shipping_address?.phone || selectedOrder.customer_phone || 'N/A'}</p>
                </div>
              </div>

              {/* Action Buttons */}
              {(selectedOrder.return_status === 'return_requested' || selectedOrder.status === 'return_requested') && (
                <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
                  <Button
                    variant="outline"
                    onClick={() => handleRejectReturn(selectedOrder)}
                    className="border-red-600 text-red-400 hover:bg-red-600 hover:text-white"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Reject Return
                  </Button>
                  <Button
                    onClick={() => handleApproveReturn(selectedOrder)}
                    disabled={processingReturn === selectedOrder.id}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {processingReturn === selectedOrder.id ? (
                      <RefreshCcw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4 mr-2" />
                    )}
                    Approve & Schedule Pickup
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
